import { type RefCallback, useCallback, useState } from 'react'
import {
  NoticeBanner,
  NoticeBannerAction,
  NoticeBannerCloseButton,
  NoticeBannerText,
} from '@/features/shell/notice-banner/NoticeBanner'
import { useTranslate } from '@/hooks/translate'

interface AnnouncementBannerContainerProps {
  ref: RefCallback<Element>
}

/**
 * Renders the announcement banner sourced from translation strings
 * (`Web.Announcement` and `Web.Announcement.Link`).
 *
 * The `ref` is used to measure the banner's height so the caller can
 * apply a page top offset (e.g. via {@link PageTopOffsetContext}).
 */
export function AnnouncementBannerContainer({
  ref,
}: AnnouncementBannerContainerProps) {
  const T = useTranslate()
  const announcement = T('Web.Announcement', '').trim() || null
  const link = T('Web.Announcement.Link', '')
  const [isClosed, setIsClosed] = useState(false)
  const close = useCallback(() => setIsClosed(true), [])

  if (!announcement || isClosed) {
    return null
  }

  const splat = link.split('|')
  let announcementLink: { label: string; url: string } | null = null
  if (splat.length >= 2) {
    const label = splat[0].trim()
    const url = splat.slice(1).join('|').trim()
    if (label && url) {
      announcementLink = { label, url }
    }
  }

  return (
    <NoticeBanner ref={ref}>
      <NoticeBannerText>
        {announcement}
        {announcementLink && (
          <AnnouncementBannerActionContainer
            label={announcementLink.label}
            url={announcementLink.url}
          />
        )}
      </NoticeBannerText>
      <NoticeBannerCloseButton onPress={close} />
    </NoticeBanner>
  )
}

interface AnnouncementBannerActionContainerProps {
  label: string
  url: string
}

function AnnouncementBannerActionContainer({
  label,
  url,
}: AnnouncementBannerActionContainerProps) {
  if (/^https?:\/\//i.exec(url)) {
    return (
      <NoticeBannerAction target="_blank" href={url}>
        {label}
      </NoticeBannerAction>
    )
  }

  if (url.startsWith('/')) {
    return <NoticeBannerAction to={url}>{label}</NoticeBannerAction>
  }

  return null
}
